import type { WarehouseOption } from '@/endpoints/integration/getIntegrationWarehouses';
import type { BuilderOption } from '@/components/astur/json-builder/types';
import { CACHE_NAMESPACES, CACHE_SWR_RETENTION_MS, CACHE_TTLS, peekCache, readCache, writeCache } from './cacheKeys';

export type TenantCacheIdentity = {
  version: 2;
  subject: string;
  organization: string;
  namespace: string;
  subscriptionId?: string;
  path?: string;
  query?: unknown;
};

const canonicalValue = (value: unknown, seen: Set<object>): unknown => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('Tenant cache values must contain finite numbers');
    return value;
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) throw new TypeError('Tenant cache values must not be circular');
    seen.add(value);
    const result = value.map((item) => canonicalValue(item, seen));
    seen.delete(value);
    return result;
  }
  if (typeof value === 'object') {
    if (seen.has(value)) throw new TypeError('Tenant cache values must not be circular');
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) throw new TypeError('Tenant cache values must be plain JSON data');
    seen.add(value);
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const item = (value as Record<string, unknown>)[key];
      if (item === undefined || typeof item === 'function' || typeof item === 'symbol' || typeof item === 'bigint') {
        throw new TypeError('Tenant cache values must be serializable JSON data');
      }
      result[key] = canonicalValue(item, seen);
    }
    seen.delete(value);
    return result;
  }
  throw new TypeError('Tenant cache values must be serializable JSON data');
};

export const canonicalJson = (value: unknown): string => JSON.stringify(canonicalValue(value, new Set()));

export const tenantCacheQualifier = (identity: TenantCacheIdentity): string =>
  canonicalJson({
    version: identity.version,
    namespace: identity.namespace,
    subscriptionId: identity.subscriptionId ?? null,
    path: identity.path ?? null,
    query: identity.query ?? null,
  });

const base = (identity: TenantCacheIdentity) => ({
  scope: 'userOrg' as const,
  namespace: identity.namespace,
  qualifier: tenantCacheQualifier(identity),
  subject: identity.subject,
  organization: identity.organization,
  storage: 'localStorage' as const,
});

export const readIntegrationUnhealthy = (identity: Omit<TenantCacheIdentity, 'namespace'>): boolean | null =>
  readCache<boolean>({ ...base({ ...identity, namespace: CACHE_NAMESPACES.integrationUnhealthy }), validate: (value) => typeof value === 'boolean' });

export const peekIntegrationUnhealthy = (identity: Omit<TenantCacheIdentity, 'namespace'>): boolean | null =>
  peekCache<boolean>({ ...base({ ...identity, namespace: CACHE_NAMESPACES.integrationUnhealthy }), validate: (value) => typeof value === 'boolean' });

export const writeIntegrationUnhealthy = (identity: Omit<TenantCacheIdentity, 'namespace'>, value: boolean): boolean =>
  writeCache({
    ...base({ ...identity, namespace: CACHE_NAMESPACES.integrationUnhealthy }),
    value,
    expiresAt: Date.now() + CACHE_TTLS.integrationUnhealthy,
  });

export type WarehouseCacheValue = { refreshedAt: number; warehouses: WarehouseOption[] };

const isWarehouseCacheValue = (value: unknown): value is WarehouseCacheValue =>
  Boolean(
    value &&
      typeof value === 'object' &&
      Number.isFinite((value as WarehouseCacheValue).refreshedAt) &&
      Array.isArray((value as WarehouseCacheValue).warehouses) &&
      (value as WarehouseCacheValue).warehouses.every(
        (warehouse) => warehouse && typeof warehouse.id === 'string' && typeof warehouse.name === 'string'
      )
  );

export const readIntegrationWarehouses = (identity: Omit<TenantCacheIdentity, 'namespace'>): WarehouseCacheValue | null =>
  readCache<WarehouseCacheValue>({
    ...base({ ...identity, namespace: CACHE_NAMESPACES.integrationWarehouses }),
    allowExpired: true,
    validate: isWarehouseCacheValue,
  });

export const peekIntegrationWarehouses = (identity: Omit<TenantCacheIdentity, 'namespace'>): WarehouseCacheValue | null =>
  peekCache<WarehouseCacheValue>({
    ...base({ ...identity, namespace: CACHE_NAMESPACES.integrationWarehouses }),
    allowExpired: true,
    validate: isWarehouseCacheValue,
  });

export const writeIntegrationWarehouses = (identity: Omit<TenantCacheIdentity, 'namespace'>, warehouses: WarehouseOption[]): boolean =>
  writeCache({
    ...base({ ...identity, namespace: CACHE_NAMESPACES.integrationWarehouses }),
    value: { refreshedAt: Date.now(), warehouses },
    expiresAt: Date.now() + CACHE_SWR_RETENTION_MS,
  });

export const readJsonBuilderOptions = (identity: Omit<TenantCacheIdentity, 'namespace'>): BuilderOption[] | null =>
  readCache<BuilderOption[]>({
    ...base({ ...identity, namespace: CACHE_NAMESPACES.jsonBuilderOptions }),
    validate: (value) => Array.isArray(value),
  });

export const writeJsonBuilderOptions = (identity: Omit<TenantCacheIdentity, 'namespace'>, options: BuilderOption[]): boolean =>
  writeCache({
    ...base({ ...identity, namespace: CACHE_NAMESPACES.jsonBuilderOptions }),
    value: options,
    expiresAt: Date.now() + CACHE_TTLS.jsonBuilderOptions,
  });
